import { Alert, App as AntApp, Button, Card, Descriptions, Drawer, Space, Statistic, Table, Tag, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { useResource } from "../useResource";
import { api, errorText } from "../api";
import { dateTime } from "../format";
import { canManage, useSession } from "../session";

type Support = "SUPPORTED" | "LIMITED" | "HOST_BRIDGE" | "SALESOS_MANAGED" | "UNSUPPORTED";
type Capability = "CATALOG" | "PRICE_STOCK" | "CART" | "ORDER" | "CAMPAIGN" | "POLICY" | "WEBHOOK";

interface Counts {
  received: number;
  valid: number;
  invalid: number;
  created: number;
  updated: number;
  unchanged: number;
  deleted: number;
}

export interface Sync {
  id: number;
  trigger: "MANUAL" | "SCHEDULED";
  status: "RUNNING" | "SUCCEEDED" | "REJECTED" | "FAILED";
  stage: string;
  counts: Counts;
  failureCode: string | null;
  failureDetail: string | null;
  startedAt: string;
  finishedAt: string | null;
}

interface Overview {
  configured: boolean;
  connectorType?: string;
  syncEnabled?: boolean;
  sectorPack?: string;
  capabilities?: Record<Capability, { support: Support; note: string }>;
  lastSync: Sync | null;
}

interface Detail {
  sync: Sync;
  issues: { sku: string | null; code: string; detail: string }[];
  issuesTruncated: boolean;
}

const CAPABILITY_LABEL: Record<Capability, string> = {
  CATALOG: "Katalog",
  PRICE_STOCK: "Fiyat ve stok",
  CART: "Sepet",
  ORDER: "Sipariş durumu",
  CAMPAIGN: "Kampanya ve kupon",
  POLICY: "İade, kargo, garanti",
  WEBHOOK: "Sipariş bildirimi",
};

const SUPPORT_TAG: Record<Support, { color: string; label: string }> = {
  SUPPORTED: { color: "green", label: "Destekleniyor" },
  LIMITED: { color: "gold", label: "Kısıtlı" },
  HOST_BRIDGE: { color: "blue", label: "Mağaza sayfası üzerinden" },
  SALESOS_MANAGED: { color: "purple", label: "SalesOS'ta yönetiliyor" },
  UNSUPPORTED: { color: "default", label: "Kapalı" },
};

const STATUS_TAG: Record<Sync["status"], { color: string; label: string }> = {
  RUNNING: { color: "processing", label: "Çalışıyor" },
  SUCCEEDED: { color: "success", label: "Tamamlandı" },
  REJECTED: { color: "warning", label: "Reddedildi" },
  FAILED: { color: "error", label: "Başarısız" },
};

/** Senkron sonucunun mağaza yöneticisine açıklaması (`docs/runbooks/catalog.md`). */
const FAILURE_TEXT: Record<string, string> = {
  EMPTY_CATALOG: "Kaynakta geçerli ürün bulunamadı.",
  TOO_MANY_INVALID: "Ürünlerin çoğu geçersiz; ayrıntılar sorun listesinde.",
  MASS_DELETION: "Yeni katalog öncekinden çok küçük; olası bir kaynak hatasına karşı mevcut katalog korundu.",
  INDEX_COUNT_MISMATCH: "Ürünler arama dizinine eksik yazıldı; senkronu yeniden çalıştırın.",
  UNREACHABLE: "Mağaza sistemine ulaşılamadı.",
  ABANDONED: "Senkron yarıda kaldı; yeniden çalıştırın.",
};

export function failureText(sync: Sync): string | null {
  if (!sync.failureCode) {
    return null;
  }
  if (sync.failureCode.startsWith("HTTP_4")) {
    return "Mağaza sistemi isteği reddetti; servis anahtarını kontrol ettirin.";
  }
  if (sync.failureCode.startsWith("HTTP_5")) {
    return "Mağaza sistemi hata verdi.";
  }
  return FAILURE_TEXT[sync.failureCode] ?? "Senkron tamamlanamadı.";
}

function StatusTag({ status }: { status: Sync["status"] }) {
  const t = STATUS_TAG[status];
  return <Tag color={t.color}>{t.label}</Tag>;
}

/** Katalog senkron durumu ve connector yetenek matrisi (v0.9 §10.1–10.2). */
export function CatalogPage() {
  const me = useSession((s) => s.me);
  const manage = canManage(me);
  const { message } = AntApp.useApp();
  const [detail, setDetail] = useState<Detail | null>(null);
  const [starting, setStarting] = useState(false);
  const { data, error, reload } = useResource(useCallback(async () => {
    const [overview, history] = await Promise.all([api.get<Overview>("/api/console/catalog"),
      api.get<Sync[]>("/api/console/catalog/syncs")]);
    return { overview, history };
  }, []));
  const overview = data?.overview ?? null;
  const history = data?.history ?? [];

  // Çalışan senkron varken durumu birkaç saniyede bir yeniler.
  const running = history.some((s) => s.status === "RUNNING");
  useEffect(() => {
    if (!running) {
      return undefined;
    }
    const timer = setInterval(reload, 3000);
    return () => clearInterval(timer);
  }, [running, reload]);

  async function start() {
    setStarting(true);
    try {
      await api.post("/api/console/catalog/syncs");
      message.success("Senkron başlatıldı");
      reload();
    } catch (e) {
      message.error(errorText(e));
    } finally {
      setStarting(false);
    }
  }

  async function open(id: number) {
    try {
      setDetail(await api.get<Detail>(`/api/console/catalog/syncs/${id}`));
    } catch (e) {
      message.error(errorText(e));
    }
  }

  if (error) {
    return <Alert type="error" title={error} showIcon />;
  }
  if (!overview) {
    return null;
  }
  if (!overview.configured) {
    return (
      <Space orientation="vertical" style={{ width: "100%" }}>
        <Typography.Title level={3}>Katalog</Typography.Title>
        <Alert type="info" showIcon title="Katalog kaynağı henüz tanımlanmadı"
          description="Mağaza sisteminizin bağlantısını SalesOS ekibi kurar. Kurulum tamamlanınca senkron burada görünür." />
      </Space>
    );
  }
  const last = overview.lastSync;
  return (
    <Space orientation="vertical" size="large" style={{ width: "100%" }}>
      <Space style={{ justifyContent: "space-between", width: "100%" }}>
        <Typography.Title level={3} style={{ margin: 0 }}>Katalog</Typography.Title>
        {manage && <Button type="primary" onClick={start} loading={starting} disabled={running}>Senkronu başlat</Button>}
      </Space>

      <Card title="Son senkron" extra={last && <StatusTag status={last.status} />}>
        {last ? (
          <Space orientation="vertical" style={{ width: "100%" }}>
            {failureText(last) && (
              <Alert type={last.status === "REJECTED" ? "warning" : "error"} showIcon title={failureText(last)} />
            )}
            <Space size="large" wrap>
              <Statistic title="Kaynaktan gelen" value={last.counts.received} />
              <Statistic title="Kabul edilen" value={last.counts.valid} />
              <Statistic title="Reddedilen" value={last.counts.invalid} />
              <Statistic title="Yeni" value={last.counts.created} />
              <Statistic title="Değişen" value={last.counts.updated} />
              <Statistic title="Kaldırılan" value={last.counts.deleted} />
            </Space>
            <Typography.Text type="secondary">
              Başlangıç {dateTime(last.startedAt)} · Bitiş {dateTime(last.finishedAt)}
              {overview.syncEnabled ? " · Otomatik senkron açık" : " · Otomatik senkron kapalı"}
            </Typography.Text>
          </Space>
        ) : (
          <Typography.Text type="secondary">Henüz senkron çalışmadı.</Typography.Text>
        )}
      </Card>

      <Card title="Senkron geçmişi">
        <Table<Sync> rowKey="id" dataSource={history} pagination={false} size="small"
          onRow={(row) => ({ onClick: () => void open(row.id), style: { cursor: "pointer" } })}
          columns={[
            { title: "Başlangıç", dataIndex: "startedAt", render: dateTime },
            { title: "Tetikleyen", dataIndex: "trigger", render: (t: Sync["trigger"]) => (t === "MANUAL" ? "Elle" : "Zamanlanmış") },
            { title: "Durum", dataIndex: "status", render: (s: Sync["status"]) => <StatusTag status={s} /> },
            { title: "Kabul / red", key: "counts", render: (_: unknown, s: Sync) => `${s.counts.valid} / ${s.counts.invalid}` },
          ]} />
      </Card>

      {overview.capabilities && (
        <Card title="Mağaza bağlantısının yetenekleri"
          extra={<Typography.Text type="secondary">Asistan kapalı yetenekleri kullanmaz</Typography.Text>}>
          <Table rowKey="key" pagination={false} size="small"
            dataSource={(Object.keys(CAPABILITY_LABEL) as Capability[])
              .filter((key) => overview.capabilities?.[key])
              .map((key) => ({ key, ...(overview.capabilities as NonNullable<Overview["capabilities"]>)[key] }))}
            columns={[
              { title: "Yetenek", dataIndex: "key", width: 200, render: (k: Capability) => CAPABILITY_LABEL[k] },
              { title: "Durum", dataIndex: "support", width: 220,
                render: (s: Support) => <Tag color={SUPPORT_TAG[s].color}>{SUPPORT_TAG[s].label}</Tag> },
              { title: "Asistan davranışına etkisi", dataIndex: "note" },
            ]} />
        </Card>
      )}

      <Drawer open={!!detail} onClose={() => setDetail(null)} size="large" title="Senkron ayrıntısı">
        {detail && (
          <Space orientation="vertical" style={{ width: "100%" }}>
            <Descriptions column={1} size="small" items={[
              { key: "status", label: "Durum", children: <StatusTag status={detail.sync.status} /> },
              { key: "stage", label: "Son adım", children: detail.sync.stage },
              { key: "started", label: "Başlangıç", children: dateTime(detail.sync.startedAt) },
              { key: "finished", label: "Bitiş", children: dateTime(detail.sync.finishedAt) },
              ...(detail.sync.failureCode ? [{ key: "failure", label: "Sonuç",
                children: `${failureText(detail.sync)} (${detail.sync.failureCode})` }] : []),
            ]} />
            <Typography.Title level={5}>Sorunlar</Typography.Title>
            {detail.issuesTruncated && (
              <Alert type="info" showIcon title="Yalnızca ilk 200 sorun gösteriliyor." />
            )}
            <Table rowKey={(r) => `${r.code}:${r.sku ?? ""}:${r.detail}`} dataSource={detail.issues} size="small"
              pagination={{ pageSize: 20 }} locale={{ emptyText: "Sorun yok" }}
              columns={[
                { title: "SKU", dataIndex: "sku", render: (s: string | null) => s ?? "—" },
                { title: "Kod", dataIndex: "code" },
                { title: "Açıklama", dataIndex: "detail" },
              ]} />
          </Space>
        )}
      </Drawer>
    </Space>
  );
}
